/* eslint-disable no-console,react/no-unused-state */
// noinspection JSUnusedGlobalSymbols,JSClosureCompilerSyntax

import React from 'react';
import PropTypes from 'prop-types';
import uniqBy from 'lodash/uniqBy';
import filter from 'lodash/filter';
import Dialog from './pages/dialog';
import Entry from '@/components/entry/entry';
import '@/public/css/reset.scss';
import '@/public/css/iconfont.scss';
import { CDN_BASE_URL, POLL_TIME, PUSH_MASS_MESSAGE_TIME } from '@/constants';
import { ApiUri, setBaseURL } from './helper';
import fetch, { setToken } from '@/lib/fetch';
import { markMsgHasBeenRead, pullMessages, pushMassMessage } from '@/api';
import SockJS from '@/lib/sock';

/**
 * 西瓜 IM App
 *
 * 业务
 * 1. 初始化历史消息
 * 2. 建立消息接受机制（iPad 时间轮训, PC websocket）
 *
 * PC 端进入页面开始处理业务
 * pad 端点击按钮开始处理业务
 *
 */

// /check/message 请求加入 eventType 参数方便统计每个模式的请求量
const EventType = {
  interval: 'interval',
  sock: 'sock',
};

/**
 * @typedef StudentInfo
 * @extends {Record<string, any>}
 * @property {string} oid
 */

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      opening: false,
      loading: false,
      methods: this.props.methods,
      showEntry: true,
      // 额外的消息，不存在于服务端，用于占位，如loading
      extraMessage: null,
      lastMessageId: null,
      /**
       * @type IMessageData
       */
      data: {
        messageSession: undefined,
        messages: [],
        newMessageCount: 0,
        online: false,
        customEnd: '',
        ...this.props.data,
      },
      marvin: {
        name: '西瓜老师',
        avatar: '',
      },
      initialized: false,
      eventType: EventType.interval,
    };
    this.audioSendRef = React.createRef();
    this.audioReceiveRef = React.createRef();

    /**
     * 学生数据
     * @type StudentInfo
     */
    this.studentInfo = undefined;
  }

  async componentDidMount() {
    const { data } = this.props;
    // 设置 baseURL
    data.baseURL && setBaseURL(data.baseURL);
    data.token && setToken(data.token);

    await this.getMarvinInfo();
    // 移动端点击按钮开始初始化
    if (!data.isMobile) {
      await this.init();
    }
  }

  /**
   * 初始化
   */
  async init() {
    // 初始化消息
    const res = await this.pullNewMessage(true);
    if (!res) {
      console.log('网络错误');
      return;
    }

    // 设置 IM 广播消息推送 timer
    this.setUpMassMessagePushInterval();

    // 前端强制开启 SOCK 模式
    if (document.cookie.includes('IM_EVENT_TYPE_SOCK')) {
      await this.onSockEvent(() => this.pullNewMessage());
      // 移动端设置轮训机制, 通过后端判断消息通知机制
    } else if (
      this.props.data.isMobile
      || res.eventType === EventType.interval
    ) {
      await this.onIntervalEvent(() => this.pullNewMessage());
      // 设置 websocket 接受消息通知机制
    } else {
      await this.onSockEvent(() => this.pullNewMessage());
    }

    this.setState({
      initialized: true,
    });
  }

  // 获取marvin
  async getMarvinInfo() {
    const resp = await this.getProfile();
    const studentOid = resp && resp.oid;
    if (studentOid) {
      const res = await this.getMarvin(studentOid);
      this.setState({
        marvin: {
          name: res && res.name,
          avatar: res && res.avatar,
        },
        showEntry: true,
      });
    }
  }

  /**
   * 拉取新消息
   * @param {boolean=} isInit
   * @return {Promise<any>}
   */
  async pullNewMessage(isInit = false) {
    const { lastMessageId } = this.state;
    let res = {};
    try {
      res = await this.requestMessageApi(lastMessageId);
    } catch (e) {
      console.error('网络错误:', e);
      return undefined;
    }

    // 没有收到新消息，不调用 markRead, 调用会导致关闭对话时候未读气泡消失
    if (!res.messages || !res.messages.length) {
      return res;
    }
    const lastMessage = res.messages[res.messages.length - 1];

    this.setState(
      (prev) => ({
        data: Object.assign(prev.data, {
          // 去掉重复消息
          messages: uniqBy(
            res.messages.length > 0
              ? prev.data.messages.concat(res.messages)
              : prev.data.messages,
            'messageId'
          ),
          newMessageCount:
            res && prev.data.newMessageCount + res.newMessageCount,
          online: res && res.online,
        }),
        lastMessageId: lastMessage.messageId,
        loading: false,
      }),
      () => {
        setTimeout(() => {
          this.scrollMessageBoxToBottom();
          if (!isInit) {
            if (lastMessage.fromMe) {
              this.playSendAudio();
            } else {
              this.playReceiveAudio();
            }
          }
        });
        this.markRead();
      }
    );

    return res;
  }

  /**
   * 从后台拉消息
   */
  requestMessageApi = (lastMessageId) => pullMessages(lastMessageId).then((res) => {
    const { messages } = res;
    window.localStorage.setItem(
      'im#my_avatar',
      messages.length > 0
        ? messages[messages.length - 1].avatar
        : 'https://m.xiguacity.cn/gutenberg/image/im-shawn-2.png'
    );
    return res;
  });

  /**
   * 获取个人信息
   * @return {Promise<StudentInfo>}
   */
  async getProfile() {
    if (this.studentInfo) return this.studentInfo;
    this.studentInfo = await fetch(ApiUri.PROFILE, {}, { method: 'POST' });
    return this.studentInfo;
  }

  // 获得marvin信息
  getMarvin(studentOid) {
    return fetch(ApiUri.OBTAIN_MARVIN, {
      studentOid,
    }, { method: 'POST' });
  }

  // 设置轮训更新消息
  onIntervalEvent(callback) {
    const pollTime = this.props.data.pollTime || POLL_TIME;
    this.setState({
      eventType: EventType.interval,
    });
    setInterval(callback, pollTime);
  }

  // 推送 IM 推广消息
  setUpMassMessagePushInterval() {
    try {
      // 初始化调用一次, 目前延迟 5S 执行
      setTimeout(() => pushMassMessage(), 5000);

      setInterval(() => {
        pushMassMessage();
      }, PUSH_MASS_MESSAGE_TIME);
    } catch (e) {
      console.log(`pushMassMessage ERROR ${e}`);
    }
  }

  // sock 服务更新消息
  async onSockEvent(callback) {
    const resp = await this.getProfile();
    const initParams = {
      onmessage: callback,
      studentId: resp.oid,
    };

    this.setState({
      eventType: EventType.sock,
    });
    if (this.props.methods && this.props.methods.getSocketInstance) {
      this.sockJS = this.props.methods.getSocketInstance(initParams);
    } else {
      this.sockJS = new SockJS(initParams);
    }
  }

  updateSendAudioStatus = () => {
    // TODO 删除该方法，在外部有调用
  };

  // 更新消息列表
  $updateMessage = (message) => {
    this.setState((prev) => {
      // 查找是否有重复ID
      if (
        prev.data.messages.find(
          (item) => item.messageId === message.message.messageId
        )
      ) {
        return undefined;
      }

      const type = message && message.message.message_content_type;
      if (
        (type === 'audio' || type === 'failure')
        && message.message.voiceId
        && !message.message.messageId
      ) {
        return undefined;
      }

      if (message) {
        this.scrollMessageBoxToBottom();
      }

      // 过滤掉没有 messageId 的消息，比如 loading
      const messages = filter(prev.data.messages, (item) => !!item.messageId);

      return {
        data: Object.assign(prev.data, {
          messages: message
            ? messages.concat(
              message.data ? message.data.message : message.message
            )
            : messages,
          online: message ? message.online : prev.data.online,
        }),
      };
    });
  };

  playSendAudio = () => {
    if (this.audioSendRef.current) {
      this.audioSendRef.current.play();
    }
  }

  playReceiveAudio = () => {
    if (this.audioReceiveRef.current) {
      this.audioReceiveRef.current.play();
    }
  }

  // 关闭对话框
  $closeDialog = () => {
    const { methods } = this.state;
    methods && methods.onCloseDialog && methods.onCloseDialog();
    this.setState({
      opening: false,
    });
  };

  // 打开对话框
  $openDialog = () => {
    this.setState(
      {
        opening: true,
      },
      async () => {
        const { methods, initialized } = this.state;
        methods && methods.onOpenDialog && methods.onOpenDialog();

        setTimeout(() => {
          this.scrollMessageBoxToBottom();
          this.markRead();
        }, 300);

        if (!initialized) {
          this.init().then();
        }
      }
    );
  };

  /**
   * 滚动聊天内容到底部、有动画
   */
  scrollMessageBoxToBottom = () => {
    setTimeout(() => {
      const div = document.querySelector('.ms-wrapper > div');
      if (div && div.scrollIntoView) {
        div.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'smooth' });
      } else {
        const node = document.querySelector('.ms-wrapper');
        if (node) node.scrollTo(0, node.scrollHeight);
      }
    });
  };

  // noinspection JSUnusedGlobalSymbols
  hideEntry = () => {
    this.setState({
      showEntry: false,
      opening: false,
    });
  };

  // noinspection JSUnusedGlobalSymbols
  showEntry = () => {
    this.setState({
      showEntry: true,
    });
  };

  toggleDialog = (value) => {
    let newValue = value;
    const { opening } = this.state;
    if (newValue === undefined) {
      newValue = !opening;
    }
    if (newValue) {
      this.$openDialog();
    } else {
      this.$closeDialog();
    }
  }

  showExtraMessage = (extraMessage) => {
    const messages = this.state.data.messages || [];
    const message = messages.find(({ fromMe }) => fromMe);
    this.setState(
      {
        extraMessage: {
          fromMe: true,
          avatar: message && message.avatar,
          contentType: 'placeholder-loading',
          ...extraMessage,
        },
      },
      () => {
        this.scrollMessageBoxToBottom();
      }
    );
  }

  clearExtraMessage = () => {
    this.setState({ extraMessage: null });
  }

  markRead() {
    const {
      opening,
      data: { messages },
    } = this.state;
    if (!messages || !messages.length || !opening) return;
    const lastMessageId = messages
      && messages[messages.length - 1]
      && !messages[messages.length - 1].error
      && messages[messages.length - 1].messageId;

    if (lastMessageId) {
      markMsgHasBeenRead(lastMessageId)
        .then(() => {
          this.setState((prev) => ({
            data: Object.assign(prev.data, {
              newMessageCount: 0,
            }),
          }));
        })
        .catch((err) => {
          // 错误信息抛出
          // eslint-disable-next-line no-console
          console.log(`mark-read error: ${err}`);
        });
    }
  }

  render() {
    return (
      <>
        {this.state.showEntry ? (
          <Entry
            $openDialog={() => this.$openDialog()}
            $closeDialog={() => this.$closeDialog()}
            opening={this.state.opening}
            data={this.state.data}
            methods={this.state.methods}
            marvin={this.state.marvin}
          />
        ) : null}
        <Dialog
          marvin={this.state.marvin}
          data={this.state.data}
          fnExtends={this.props.fnExtends}
          loading={this.state.loading}
          methods={this.state.methods}
          $closeDialog={this.$closeDialog}
          opening={this.state.opening}
          $updateMessage={this.$updateMessage}
          extraMessage={this.state.extraMessage}
          toggleDialog={this.toggleDialog}
          showExtraMessage={this.showExtraMessage}
          clearExtraMessage={this.clearExtraMessage}
        />
        <audio ref={this.audioSendRef} src={`${CDN_BASE_URL}/audio-send.wav`} />
        <audio ref={this.audioReceiveRef} src={`${CDN_BASE_URL}/audio-receive.mp3`} />
      </>
    );
  }
}

App.propTypes = {
  data: PropTypes.shape({
    pollTime: PropTypes.number,
    csrf: PropTypes.string,
    isMobile: PropTypes.bool,
    baseURL: PropTypes.string,
    token: PropTypes.string,
    customEnd: PropTypes.string,
  }),
  fnExtends: PropTypes.func,
  methods: PropTypes.shape({
    onCloseDialog: PropTypes.func,
    onOpenDialog: PropTypes.func,
    getSocketInstance: PropTypes.func,
    // todo maybe has more
  }),
};

export default App;
